import { useState, useEffect } from "react";
import { useLazyQuery, useQuery } from "@apollo/client";
import { ELIGIBLE_FOR_TERMINATION_COUNT_QUERY, PEOPLE_QUERY } from "../queries";
import { Person } from "@/types";
import EmptyState from "@/components/EmptyState";
import Table, { TableDetails, type Row } from "@/components/Table";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import usePagination from "@/hooks/usePagination";
import { isNull } from "lodash";
import { useNavigate, Path, Params } from "@/router";
import PeopleTableFilters from "./PeopleTableFilters";
import MetricGrid from "@/components/MetricGrid";
import { LinkProps } from "@generouted/react-router/client";
import { PageInfo } from "@/components/Pagination";
import { useParams } from "@/hooks/useSearchParams";
import { generatePath } from "react-router-dom";
import { buildColumns } from "@/pages/billing/_utils";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { showSnackbar } from "@/lib/snackbarUtils";
import { buildGraphqlInput, mapPeople, type ReportType, type Variables } from "../_utils/peopleReportMapping";
import { exportPeopleCsv, fetchAllPeopleForExport } from "../_utils/exportPeopleCsv";

export type { ReportType, Variables };

export interface PeopleRow {
  id: string;
  person: Person;
  pgaId?: string;
}

type StatsType = {
  totalCount: number | null;
  totalEligibleForTermination: number | null;
};

type PeopleData = {
  people: {
    nodes: Person[];
    totalCount: number;
    pageInfo: PageInfo;
  };
};

type EligibleForTerminationData = {
  availableForTerminationsDuesOrders: {
    totalCount: number;
  };
};

export const PeopleTable = <P extends Path>({
  reportType,
  defaultVariables = {}
}: {
  reportType: ReportType;
  defaultVariables?: Variables;
}) => {
  useRequireCapability({ domain: DOMAIN.COMMERCE, permission: PERMISSION.viewOrders });
  const isSuspended = reportType === "suspensions";
  const isTerminations = reportType === "terminations";
  let { params, searchParamsUrl } = useParams();
  const { isCompactLayout } = useIsCompactLayout();

  const { renderFooter, paginationVariables } = usePagination();

  const normalizedParams = (() => {
    const next = { ...params } as Record<string, unknown>;
    if (typeof next.reestablished === "string") {
      if (next.reestablished === "true") next.reestablished = true;
      else if (next.reestablished === "false") next.reestablished = false;
      else delete next.reestablished;
    }
    return next;
  })();

  const [variables, setVariables] = useState<Variables>({ ...defaultVariables, ...normalizedParams });
  const [exporting, setExporting] = useState(false);
  const { data, loading } = useQuery<PeopleData>(PEOPLE_QUERY, {
    variables: {
      input: buildGraphqlInput(variables),
      includeSuspensions: isSuspended,
      includeTerminations: isTerminations,
      ...paginationVariables
    }
  });

  const [fetchPeoplePage] = useLazyQuery<PeopleData>(PEOPLE_QUERY, {
    fetchPolicy: "network-only"
  });

  // Do not block the people table on this expensive eligibility count.
  const {
    data: eligibleData,
    loading: eligibleLoading,
    error: eligibleError
  } = useQuery<EligibleForTerminationData>(ELIGIBLE_FOR_TERMINATION_COUNT_QUERY, { skip: !isSuspended });

  // mapPeople shapes a display row; Table columns only need the person name/photo fields.
  const peopleData = mapPeople(data?.people?.nodes || []) as unknown as PeopleRow[];

  const navigate = useNavigate();

  function onClickRow(row: Row<PeopleRow>) {
    navigate(
      generatePath(
        `/people/:pgaId/person/contact?return_to=/billing/dues-management/reporting/${
          isSuspended ? "suspensions" : "terminations"
        }&${searchParamsUrl}`,
        { pgaId: row.original.pgaId! }
      ) as Path
    );
  }

  const [stats, setStats] = useState<StatsType>({
    totalCount: null,
    totalEligibleForTermination: null
  });

  useEffect(() => {
    if (!loading && data?.people && stats.totalCount === null) {
      setStats((prev) => ({
        ...prev,
        totalCount: data.people.totalCount
      }));
    }
  }, [loading, data, stats.totalCount]);

  useEffect(() => {
    if (!isSuspended) return;
    if (!eligibleLoading && eligibleData && stats.totalEligibleForTermination === null) {
      setStats((prev) => ({
        ...prev,
        totalEligibleForTermination: eligibleData.availableForTerminationsDuesOrders.totalCount
      }));
    }
  }, [isSuspended, eligibleLoading, eligibleData, stats.totalEligibleForTermination]);

  const handleExportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { nodes, truncated } = await fetchAllPeopleForExport({
        fetchPage: async (options) => {
          const result = await fetchPeoplePage(options);
          return {
            data: result.data,
            error: result.error as Error | undefined
          };
        },
        input: buildGraphqlInput(variables),
        reportType
      });

      if (nodes.length === 0) {
        showSnackbar("No people to export for the current filters");
        return;
      }

      const count = exportPeopleCsv({ nodes, reportType, variables });
      const noun = count === 1 ? "person" : "people";
      showSnackbar(
        truncated
          ? `Exported ${count} ${noun} (page limit reached; some rows may be missing)`
          : `Exported ${count} ${noun}`
      );
    } catch {
      showSnackbar("Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  const reestablishedFilter = variables.reestablished;
  const metricLabel = isSuspended
    ? "Total Suspended People (Unpaid Dues)"
    : reestablishedFilter === true
      ? "Total Re-established After Non-Payment"
      : reestablishedFilter === false
        ? "Total Currently Terminated (Unpaid Dues)"
        : "Total Non-Payment Terminations";

  const emptyTitle = isSuspended ? "No Suspended People from Dues Billing" : "No Non-Payment Terminations Found";
  const metricCount = data?.people?.totalCount ?? stats.totalCount;
  const eligibleCount =
    eligibleData?.availableForTerminationsDuesOrders.totalCount ?? stats.totalEligibleForTermination;
  const eligibleMetricLoading = !eligibleError && isNull(eligibleCount);
  const eligibleMetricValue = eligibleError ? "—" : `${!isNull(eligibleCount) && eligibleCount}`;
  const exportLabel = exporting ? "Exporting…" : "Export CSV";

  return (
    <>
      {stats.totalCount === 0 ? (
        <EmptyState title={emptyTitle} iconName="group_off" />
      ) : (
        <>
          <MetricGrid
            metrics={[
              {
                label: metricLabel,
                value: `${!isNull(metricCount) && metricCount}`,
                loading: isNull(stats.totalCount),
                iconName: "person"
              },
              ...(isSuspended
                ? [
                    {
                      label: `Total Eligible for Termination`,
                      value: eligibleMetricValue,
                      loading: eligibleMetricLoading,
                      link: { to: "/billing/dues-management/suspensions-and-terminations/terminations" } as LinkProps<
                        P,
                        Params
                      >
                    }
                  ]
                : [])
            ]}
            layout="expanded"
          />

          <div className="w-full">
            <Table
              data={peopleData || []}
              loading={loading}
              onClickRow={onClickRow}
              renderDetails={() => {
                const bodyMessage = isSuspended
                  ? "View all persons who have been suspended due to non-payment of Dues in the current billing cycle."
                  : "View Members, Associates, and Affiliates terminated for non-payment of dues. Filter by program, termination date, and re-establishment to support Canvas access removal and restore.";
                const heading = isSuspended ? "Dues Non-Payment Suspensions" : "Dues Non-Payment Terminations";
                return (
                  <TableDetails
                    heading={heading}
                    body={bodyMessage}
                    actions={[
                      {
                        label: exportLabel,
                        variant: "text",
                        icon: "download",
                        disabled: exporting || loading,
                        onClick: handleExportCsv,
                        testId: "export-people-csv-button"
                      }
                    ]}
                  />
                );
              }}
              renderHeader={() => (
                <PeopleTableFilters
                  reportType={reportType}
                  defaultVariables={defaultVariables}
                  peopleData={peopleData}
                  variables={variables}
                  setVariables={setVariables}
                  data={data}
                />
              )}
              columns={[
                ...buildColumns<PeopleRow>({
                  isCompactLayout,
                  hasCheckbox: false
                }),
                { accessorKey: "pgaId", header: "PGA ID", enableSorting: false },
                { accessorKey: "classification", header: "Class", enableSorting: false },
                { accessorKey: "status", header: "Status", enableSorting: false },
                { accessorKey: "section", header: "Section", enableSorting: false },
                { accessorKey: "primaryFacility", header: "Primary Facility", enableSorting: false },
                ...(isSuspended
                  ? [{ accessorKey: "suspensionsDate", header: "Suspension Date", enableSorting: false }]
                  : [
                      { accessorKey: "terminationsDate", header: "Termination Date", enableSorting: false },
                      ...(isTerminations && reestablishedFilter !== false
                        ? [{ accessorKey: "reestablishedDate", header: "Re-established Date", enableSorting: false }]
                        : [])
                    ])
              ]}
              renderFilteredEmptyState={() => (
                <EmptyState title="No Results Found" caption="Try a different search or filter" iconName="person" />
              )}
              renderFooter={() => (
                <div className={` flex justify-end flex-col md:flex-row md:items-center px-3`}>
                  {renderFooter(data?.people, {
                    totalCount: data?.people?.totalCount || 0,
                    pageCount: peopleData.length
                  })()}
                </div>
              )}
            />
          </div>
        </>
      )}
    </>
  );
};

export default PeopleTable;
